




















































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import AppSettings from "arc4u.framework.js/token/AppSettings";
import IAppSettings from "arc4u.framework.js/token/IAppSettings";
import TokenKeys from "arc4u.framework.js/token/TokenKeys";

import i18n from "@/GlobalResource";
import getBrowserLocale, { getChoosenLanguage } from "@/lib/locale";
import { registerForm } from "@/fixtures/register";
import * as Validation from "@/lib/validation";
import { FieldRules, FormRegister } from "@/types";
import {
  Language,
  Contact,
  Competence,
  ApiException,
} from "@/proxy/Prequalification.CandidateMgt.FacadeClient";
import { AgreementPublicClient } from "@/proxy/Prequalification.Legal.FacadeClient";
import { DownloadDocumentFrom64 } from "@/lib/documents";
import { actionTypes as referenceDataActionTypes } from '@/store/module/referenceData/actions';
import { mutationTypes as errorsMutationTypes } from '@/store/module/errors/mutations';

import RegisterFormCompany from "./RegisterFormCompany.vue";
import RegisterFormContacts from "./RegisterFormContacts.vue";

const register = namespace("register");
const referenceData = namespace('referenceData');
const errors = namespace('errors');

@Component({
  components: {
    RegisterFormCompany,
    RegisterFormContacts,
  },
})
export default class RegisterStepper extends Vue {
  @Prop({ type: Boolean, default: true }) valid!: boolean;

  @register.State(state => state.step)
  readonly step!: number;

  @register.Mutation("gotoStep")
  gotoStep: any;

  @referenceData.Action(referenceDataActionTypes.fetchAll)
  fetchAllReferenceData!: () => void;

  @errors.Mutation(errorsMutationTypes.setErrors)
  setErrors!: (values: string[]) => void;

  registerForm: FormRegister = registerForm;
  isStep1Valid: boolean = false;
  isStep2Valid: boolean = false;
  i18n: any = i18n;
  stepWrappers: HTMLElement[] | null = null;
  scrollTopOffset: number = 0;

  isDownloading: boolean = false;

  @referenceData.State(state => state.competences)
  readonly competences!: Competence[];

  get currentStep() {
    return this.step;
  }

  set currentStep(value) {
    this.gotoStep(value);
  }

  get rules(): FieldRules {
    const rules = {
      // step 1
      companyName: [
        Validation.isFilled("register.company.errors.required.name"),
        Validation.hasMaximumLength(50, "register.company.errors.length.name")
      ],
      companyStreet: [
        Validation.isFilled("register.company.errors.required.street"),
        Validation.hasMaximumLength(80, "register.company.errors.length.street")
      ],
      companyHouseNumber: [
        Validation.isFilled("register.company.errors.required.number"),
        Validation.hasMaximumLength(10, "register.company.errors.length.number")
      ],
      companyZip: [
        Validation.isFilled("register.company.errors.required.zip"),
        Validation.hasMaximumLength(20, "register.company.errors.length.zip")
      ],
      companyCity: [
        Validation.isFilled("register.company.errors.required.city"),
        Validation.hasMaximumLength(50, "register.company.errors.length.city")
      ],
      companyCountry: [Validation.isFilled("register.company.errors.required.country")],
      companyTelephone: [Validation.hasMaximumLength(50, "register.company.errors.length.phone")],
      companyFax: [Validation.hasMaximumLength(50, "register.company.errors.length.fax")],
      companyRegistrationNumber: [
        Validation.isFilled("register.company.errors.required.registrationNumber"),
        Validation.hasMaximumLength(14, "register.company.errors.length.registrationNumber"),
        Validation.isAlphaNumerics("register.company.errors.type.alphanumerics"),
        Validation.isStartingWithCountryCode("register.company.errors.type.countrycode")
      ],
      companyBusinessNumber: [
        Validation.isFilled("register.company.errors.required.businessnumber"),
        Validation.hasMaximumLength(20, "register.company.errors.length.businessnumber")
      ],
      legalStatus: [Validation.isFilled("register.company.errors.required.legalstatus")],

      // step 2
      contactCivilStatus: [Validation.isFilled("register.contacts.errors.required.civilstatus")],
      contactFirstname: [
        Validation.isFilled("register.contacts.errors.required.firstName"),
        Validation.hasMaximumLength(50, "register.contacts.errors.length.firstName")
      ],
      contactLastname: [
        Validation.isFilled("register.contacts.errors.required.lastName"),
        Validation.hasMaximumLength(50, "register.contacts.errors.length.lastName")
      ],
      contactLanguage: [Validation.isFilled("register.contacts.errors.required.language")],
      contactFunction: [
        Validation.isFilled("register.contacts.errors.required.function"),
        Validation.hasMaximumLength(50, "register.contacts.errors.length.function")
      ],
      contactTelephone: [Validation.hasMaximumLength(50, "register.contacts.errors.length.phone")],
      contactMobile: [
        Validation.isFilled("register.contacts.errors.required.mobile"),
        Validation.hasMaximumLength(50, "register.contacts.errors.length.mobile")
      ],
      contactEmail: [
        Validation.isFilled("register.contacts.errors.required.email"),
        Validation.isEmail("register.contacts.errors.type.email"),
        Validation.hasMaximumLength(60, "register.contacts.errors.length.email")
      ],
      contactFor: [Validation.checkAtLeastOne("register.contacts.errors.required.contactFor")],
      conditionsGDPR: [Validation.isChecked("register.contacts.errors.required.gdpr")]
    };

    return rules;
  }

  get isCurrentStepValid(): boolean {
    if (this.currentStep === 1)
      return this.isStep1Valid;

    if (this.currentStep === 2)
      return this.isStep2Valid;

    return true;
  }

  @Watch("currentStep")
  onCurrentStepChange(value: number, oldValue: number): void {
    this.$nextTick(() => {
      const index = Math.max(value - 1, 0);

      this.stepWrappers?.[index]?.scrollTo(0, 0);
      this.scrollTopOffset = 0;
    });
  }

  @Watch("isCurrentStepValid")
  onIsCurrentStepValidChange(value: boolean): void {
    this.validate();
  }

  async created(): Promise<void> {
    i18n.locale = getChoosenLanguage() ?? getBrowserLocale({
      countryCodeOnly: true,
      fallbackLocale: i18n.fallbackLocale.toString(),
    });

    this.fetchAllReferenceData();
  }

  mounted() {
    this.gotoStep(1);
    this.stepWrappers = Array.from(document.querySelectorAll(".v-stepper__wrapper"));

    this.addScrollListeners();
  }

  beforeDestroy() {
    this.removeScrollListeners();
  }

  onScroll(event: any) {
    this.scrollTopOffset = event.target?.scrollTop || 0;
  }

  addScrollListeners() {
    this.stepWrappers?.forEach(el => el.addEventListener("scroll", this.onScroll));
  }

  removeScrollListeners() {
    this.stepWrappers?.forEach(el => el.removeEventListener("scroll", this.onScroll));
  }

  displayError(error: any) {
    const msg: string[] = [];

    if (error instanceof ApiException) {
      let errors = JSON.parse(error.response)?.errors;
      if (errors) {
        Object.keys(errors).forEach(function(key, index) {
          if (errors[key] && Array.isArray(errors[key]) && errors[key].length > 0) {
            msg.push(errors[key][0]);
          }
        });
      }
    }

    if (msg.length)
      this.setErrors(msg);
    else
      this.setErrors(['general.error']);
  }

  validate(): void {
    const allSelectedAtLeastOnce = Validation.checkAllSelectedAtLeastOnce(
      this.competences.map(c => c.code ?? ""),
      this.registerForm.candidate.contacts?.flatMap(c => c.competences!) ?? []
    );

    const singleInvestment = !this.isInvestmentFilesSelectedMoreThanOnce(
      this.registerForm.candidate.contacts ?? []
    );

    const contactsHaveAtLeastOneCompetence = this.registerForm.errors.contactFor.errors.filter(e => e).length === 0;
    this.registerForm.errors.allCompetences.error = !allSelectedAtLeastOnce;
    this.registerForm.errors.singleinvestment.error = !singleInvestment;

    this.$emit(
      "update:valid",
      this.isCurrentStepValid &&
        (this.currentStep !== 2 ||
          (allSelectedAtLeastOnce && singleInvestment && contactsHaveAtLeastOneCompetence))
    );
  }

  isInvestmentFilesSelectedMoreThanOnce(contacts: Contact[]): boolean {
    return contacts.flatMap(c => c.competences).filter(c => c === "cbInvestmentFile").length > 1;
  }

  async onDownloadGDPR() {
    this.isDownloading = true;
    try {
      let settings: IAppSettings = AppSettings.Get("OAuth");
      var proxy = new AgreementPublicClient(
        settings.Values.filter(v => v.Key === TokenKeys.RootServiceUrlKey)[0].Value
      );
      var result = await proxy.getGDPRConditions();
      DownloadDocumentFrom64(result!.content, "Privacy Policy", "pdf");
    } catch (e) {
      this.displayError(e);
    }

    this.isDownloading = false;
  }
}
